import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  useTheme,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const QuizInstructionPage = () => {
  const theme = useTheme();
  const { quizId } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem("student_token");
  const [quizDetails, setQuizDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState("english");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    const fetchQuizDetails = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
        const res = await axios.get(`${API_BASE_URL}/quiz/${quizId}/questions/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setQuizDetails(res.data);
      } catch (err) {
        console.error("Error fetching quiz details:", err);
        setSnackbarMessage("Failed to load quiz details.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    fetchQuizDetails();
  }, [quizId, token, navigate]);

  const handleStartQuiz = () => {
    navigate(`/quiz/${quizId}`);
  };

  if (loading)
    return (
      <Box
        sx={{
          textAlign: "center",
          mt: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "#667eea" }} />
        <Typography
          variant="h6"
          mt={2}
          sx={{ color: theme.palette.text.secondary, fontWeight: "600" }}
        >
          Loading quiz instructions...
        </Typography>
      </Box>
    );

  if (!quizDetails)
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Failed to load quiz details.
        </Typography>
      </Box>
    );

  const instructions = {
    english: [
      {
        title: "Quiz Overview",
        points: [
          `This quiz contains ${quizDetails.questions.length} questions.`,
          `You will have ${quizDetails.time_limit_minutes || "unlimited"} minutes to complete the quiz.`,
          `There is ${quizDetails.neg_marking ? "negative marking" : "no negative marking"} for incorrect answers.`,
        ],
      },
      {
        title: "Submission Rules",
        points: [
          "Only the answers you save and move to the next question will be submitted.",
          "You can submit the quiz directly without attempting all questions. In that case, only the attempted answers will be submitted.",
          "Once submitted, you cannot change your answers.",
        ],
      },
      {
        title: "Navigation",
        points: [
          "Use the 'Previous' and 'Next' buttons to navigate between questions.",
          "You can mark questions for review and return to them later.",
          "You can skip questions and return to them before submitting.",
        ],
      },
      {
        title: "Proctoring",
        points: [
          "Do not switch tabs or minimize the window during the quiz. Doing so may result in automatic submission.",
          "Right-click and certain keyboard shortcuts are disabled during the quiz.",
        ],
      },
    ],
    hindi: [
      {
        title: "क्विज़ अवलोकन",
        points: [
          `इस क्विज़ में ${quizDetails.questions.length} प्रश्न हैं।`,
          `क्विज़ पूरा करने के लिए आपके पास ${quizDetails.time_limit_minutes || "असीमित"} मिनट होंगे।`,
          `गलत उत्तरों के लिए ${quizDetails.neg_marking ? "नकारात्मक अंकन" : "कोई नकारात्मक अंकन नहीं"} है।`,
        ],
      },
      {
        title: "सबमिशन नियम",
        points: [
          "केवल वे उत्तर सबमिट किए जाएंगे जिन्हें आप सेव करते हैं और अगले प्रश्न पर जाते हैं।",
          "आप सभी प्रश्नों का प्रयास किए बिना क्विज़ सबमिट कर सकते हैं। उस स्थिति में, केवल प्रयास किए गए उत्तरों को सबमिट किया जाएगा।",
          "एक बार सबमिट करने के बाद, आप अपने उत्तरों को बदल नहीं सकते।",
        ],
      },
      {
        title: "नेविगेशन",
        points: [
          "प्रश्नों के बीच नेविगेट करने के लिए 'पिछला' और 'अगला' बटन का उपयोग करें।",
          "आप प्रश्नों को समीक्षा के लिए चिह्नित कर सकते हैं और बाद में उन पर लौट सकते हैं।",
          "आप प्रश्नों को छोड़ सकते हैं और सबमिट करने से पहले उन पर लौट सकते हैं।",
        ],
      },
      {
        title: "निगरानी",
        points: [
          "क्विज़ के दौरान टैब स्विच न करें या विंडो को मिनिमाइज़ न करें। ऐसा करने पर स्वचालित सबमिशन हो सकता है।",
          "क्विज़ के दौरान राइट-क्लिक और कुछ कीबोर्ड शॉर्टकट अक्षम हैं।",
        ],
      },
    ],
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        mx: "auto",
        minHeight: "100vh",
        background: theme.palette.background.default,
        borderRadius: "24px",
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: "800",
            background: theme.palette.mode === 'light'
              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          QUIZ INSTRUCTIONS
        </Typography>
      </Box>
      <Paper
        sx={{
          p: 4,
          borderRadius: "16px",
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
          border: `1px solid ${theme.palette.divider}`,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel sx={{ color: theme.palette.text.primary }}>Language</InputLabel>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              label="Language"
              sx={{
                borderRadius: "12px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#667eea",
                },
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              }}
            >
              <MenuItem value="english">English</MenuItem>
              <MenuItem value="hindi">हिंदी</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {instructions[language].map((section, index) => (
          <Box key={index} sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "700",
                color: theme.palette.text.primary,
                mb: 2,
                borderBottom: `2px solid ${theme.palette.divider}`,
                pb: 1,
              }}
            >
              {section.title}
            </Typography>
            {section.points.map((point, i) => (
              <Typography
                key={i}
                variant="body1"
                sx={{
                  mb: 1,
                  color: theme.palette.text.primary,
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Chip
                  label={i + 1}
                  size="small"
                  sx={{
                    bgcolor: "#667eea",
                    color: "white",
                    fontWeight: "bold",
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                  }}
                />
                {point}
              </Typography>
            ))}
          </Box>
        ))}
        <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleStartQuiz}
            sx={{
              borderRadius: "12px",
              padding: "12px 36px",
              fontWeight: "600",
              textTransform: "none",
              fontSize: "1rem",
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              "&:hover": {
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                  : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
              },
            }}
          >
            Start Quiz
          </Button>
        </Box>
      </Paper>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            fontSize: "1rem",
            fontWeight: "600",
            borderRadius: "12px",
            boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
            backgroundColor: theme.palette.mode === 'light' ? "#f44336" : "#d32f2f",
            color: "white",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuizInstructionPage;
